import type { DynamicDataTargetType } from "replo-runtime/shared/dynamicData";
import type {
  MetafieldDefinition,
  MetafieldType,
  SwatchValue,
} from "replo-runtime/shared/types";
import type { Context } from "replo-runtime/store/ReploVariable";
import type { Formatter } from "schemas/dynamicData";

import React from "react";

import uniq from "lodash-es/uniq";
import {
  Palette as ColorIcon,
  DollarSign as CurrencyIcon,
  Hash as HashIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  List as ListIcon,
  ShoppingBag as ProductIcon,
  Type as TextIcon,
} from "lucide-react";
import { isDynamicDesignLibraryValue } from "replo-runtime/shared/utils/designLibrary";
import { extractUpdatedDefaultDynamicDataPriceInfo } from "replo-runtime/shared/utils/dynamic-data";
import {
  extractHandlebarsContent,
  findBalancedDoubleBrackets,
} from "replo-runtime/store/ReploVariable";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { formatterSchema } from "schemas/dynamicData";
import { z } from "zod";

type DynamicDataType =
  | "text"
  | "price"
  | "image"
  | "list"
  | "number"
  | "product"
  | "color"
  | "url";

// Type for the allowed attributes in the dynamic data tree
export type DynamicDataAttribute = {
  key: string;
  displayName: string;
  description?: string;
  type: DynamicDataType;
};

// Type for the dynamic data tree category
type DynamicDataTreeCategory = {
  categoryName: string;
  categoryDescription?: string;
  // NOTE (Jackson, 2025-03-25): parentKey is null if we need to select data
  // from the top level of the attributes hierarchy
  parentKey: string | null;
  allowedAttributes: DynamicDataAttribute[];
};

function extractSwatchImageLists(
  swatchesObj: Record<string, SwatchValue>,
  parentKey: string,
  optionName?: string,
) {
  return Object.entries(swatchesObj ?? {}).reduce(
    (imageLists: DynamicDataAttribute[], [swatchName, swatchValue]) => {
      if (swatchValue?.imageList) {
        imageLists.push({
          key: `${parentKey}.${optionName ? `${optionName}.` : ""}_swatches.${swatchName}.imageList`,
          displayName: `${swatchName} Swatch Image List`,
          description: `Images for ${swatchName} swatch`,
          type: "image",
        });
      }
      return imageLists;
    },
    [],
  );
}

function extractSwatchImageAndColors(
  swatchesObj?: Record<string, SwatchValue>,
) {
  if (!swatchesObj) {
    return null;
  }
  return Object.entries(swatchesObj).reduce(
    (swatchesData, [swatchName, swatchValue]) => {
      if (swatchValue.color) {
        swatchesData.colors.push(swatchName);
      }
      if (swatchValue.image) {
        swatchesData.images.push(swatchName);
      }
      return swatchesData;
    },
    { images: [], colors: [] } as {
      images: string[];
      colors: string[];
    },
  );
}

// NOTE (Matt 2025-03-07): We need to sort the formatters to get this ideal order.
// The reason being that some formatters make sense to apply before others,
// particularly when it comes to price formatting.
const idealFormatterOrder: Formatter["type"][] = [
  "quantityMultiplier",
  "discount",
  "rounded",
  "currency",
  "date",
];

const isBuyBoxRelatedFormatter = (formatterType: Formatter["type"]) => {
  return exhaustiveSwitch({ type: formatterType })({
    quantityMultiplier: true,
    discount: true,
    rounded: false,
    currency: false,
    date: false,
  });
};

const VARIANT_SPECIFIC_DYNAMIC_DATA_PATHS = [
  "_variant",
  "_currentVariant",
  "_currentOption",
];

// NOTE (Matt 2025-05-29): There are some "buybox options" formatter types
// that we want to only allow in specific circumstances. Mainly if the data
// relates to a variant (as opposed to relating to a selling plan)
export const shouldAllowBuyboxOptionsByDynamicDataPath = (path?: string) => {
  return (
    path &&
    VARIANT_SPECIFIC_DYNAMIC_DATA_PATHS.some((pathKey) => {
      return path.includes(pathKey);
    })
  );
};

export const updateFormatters = (
  formatters: Formatter[],
  dynamicDataString: string,
) => {
  const parsedFormatters = z
    .object({
      formatters: z.array(formatterSchema),
    })
    .safeParse({ formatters });
  if (parsedFormatters.error) {
    return null;
  }
  let matches = findBalancedDoubleBrackets(dynamicDataString);
  matches = uniq(matches);
  let result = dynamicDataString;
  if (!matches) {
    return result;
  }
  for (const match of matches) {
    let [oldPath] = extractHandlebarsContent(match)?.split("|") ?? [];
    oldPath = oldPath?.trim();
    if (!oldPath) {
      continue;
    }
    if (!oldPath || isDynamicDesignLibraryValue(oldPath)) {
      continue;
    }
    const updatedDefaults = extractUpdatedDefaultDynamicDataPriceInfo(oldPath);
    const path = updatedDefaults ? updatedDefaults.path : oldPath;
    let newDynamicDataReference = `{{ ${path} }}`;
    const shouldAllowBuyboxOptions =
      shouldAllowBuyboxOptionsByDynamicDataPath(path);

    if (formatters && formatters.length > 0) {
      const sortedFormatters = formatters
        .sort((a, b) => {
          return (
            idealFormatterOrder.indexOf(a.type) -
            idealFormatterOrder.indexOf(b.type)
          );
        })
        .filter((formatter) => {
          return (
            shouldAllowBuyboxOptions ||
            !isBuyBoxRelatedFormatter(formatter.type)
          );
        });

      const formattersString = JSON.stringify({ formatters: sortedFormatters });
      newDynamicDataReference = `{{ ${path} | formatters:${formattersString} }}`;
    }
    result = result.replaceAll(match, newDynamicDataReference);
  }
  return result;
};

export const getPathFromVariable = (variable: string | undefined): string[] => {
  // Note (Noah, 2022-01-17, REPL-6017): replace out any content outside of the {{ }},
  // like closing <p> tags for RTE content
  return (
    variable?.replace(/.*{{/, "").replace(/}}.*/, "").split(".").slice(1) ?? []
  );
};

const SUPPORTED_METAFIELD_TYPES = new Map<MetafieldType, string>([
  ["color", "Color"],
  ["multi_line_text_field", "Multi-line text"],
  ["number_decimal", "Decimal number"],
  ["number_integer", "Integer number"],
  ["single_line_text_field", "Single-line text"],
  ["url", "URL"],
  ["file_reference", "File reference"],
  ["rich_text_field", "Rich Text"],
]);

const textMetafieldTypes = new Set<MetafieldType>([
  "multi_line_text_field",
  "single_line_text_field",
  "rich_text_field",
  "number_decimal",
  "number_integer",
]);

// NOTE (Jackson, 2025-03-26): While url can techincally be anything and not just an image src, we're including
// it here and leaving it up to the user to ensure that the url is an image src. We'd rather not do some sort of
// leaky validation at this time but can revisit this later
const imageMetafieldTypes = new Set<MetafieldType>(["file_reference", "url"]);

/**
 * Generates a one or two layered tree structure of dynamic data options
 * based on the context and target data type
 *
 * @param componentAttributes - The attributes of the selected component(s)
 * @param targetDataType - The type of data being targeted, used for icon selection
 * @param productMetafields - Product metafield definitions
 * @param variantMetafields - Variant metafield definitions
 * @param showAdvancedAttributes - Boolean. Indicates whether we should be included attributes like "product.id" or "variant.sku"
 * @returns One or more DynamicDataTreeCategory objects, where more than one category indicates
 * a multi-layered tree structure
 *
 * Use null for parentKey to display top level options
 */
export const getDynamicDataTree = (
  componentAttributes: Context["attributes"],
  targetDataType: DynamicDataTargetType,
  productMetafields: MetafieldDefinition[],
  variantMetafields: MetafieldDefinition[],
  showAdvancedAttributes?: boolean,
): DynamicDataTreeCategory[] => {
  if (!componentAttributes) {
    return [];
  }

  // Filter supported metafields by type
  const supportedProductMetafields = productMetafields.filter((def) => {
    return SUPPORTED_METAFIELD_TYPES.has(def.type);
  });

  const supportedVariantMetafields = variantMetafields.filter((def) => {
    return SUPPORTED_METAFIELD_TYPES.has(def.type);
  });

  // TODO (Matt 2025-03-17): This tree fn is just setting up support for targetDataType TEXT right now
  // to allow for the initial build of the new DynamicData UI. There is a REPL-16545 for adding the
  // necessary data for the other targetTypes.

  // NOTE (Matt 2025-03-25): There are some keys with the keyword "current" that are used when we
  // render an item in a list of items (ie a variant in a list of variants). In some cases, it's
  // an abstract key like "currentItem" and others it's a specifc key like "currentVariant". We
  // need to support both.
  const parentKey = [
    "_currentVariant",
    "_currentSellingPlan",
    "_currentItem",
    "_currentTabItem",
  ].find((key) => key in componentAttributes) as
    | keyof typeof componentAttributes
    | undefined;

  // #region COUNTDOWN TIMER DYNAMIC DATA
  // If _endTime is present, we are in a countdown timer component
  if (componentAttributes._endTime) {
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Countdown Timer Data",
            parentKey: null,
            allowedAttributes: [
              {
                key: "daysUntilEnd",
                displayName: "Days Until End",
                type: "number",
              },
              {
                key: "hoursUntilEnd",
                displayName: "Hours Until End",
                type: "number",
              },
              {
                key: "minutesUntilEnd",
                displayName: "Minutes Until End",
                type: "number",
              },
              {
                key: "secondsUntilEnd",
                displayName: "Seconds Until End",
                type: "number",
              },
            ],
          },
        ];
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [];
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/url": (): DynamicDataTreeCategory[] => {
        return [];
      },
      list: (): DynamicDataTreeCategory[] => {
        return [];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        return [];
      },
      image: (): DynamicDataTreeCategory[] => {
        return [];
      },
    });
  }
  // #endregion

  // #region CURRENT ITEM IMAGE CAROUSEL
  // NOTE (Matt 2025-03-27): If CurrentItem is a string instead of an object, and there is the `_carouselSlides` key
  // in componentAttributes, then we're inside a carousel and the only option should be the currentItem for Image.
  if (
    typeof componentAttributes._currentItem === "string" &&
    "_carouselSlides" in componentAttributes &&
    targetDataType === "image"
  ) {
    return [
      {
        categoryName: "Image",
        parentKey: null,
        allowedAttributes: [
          {
            key: "_currentItem",
            displayName: "Carousel Image",
            type: "image",
          },
        ],
      },
    ];
  }
  // #endregion

  // #region VARIANT LIST DYNAMIC DATA
  // If _currentVariant is present, we are in a repeated variant list
  if (parentKey && componentAttributes[parentKey]?.productId) {
    const swatchData = extractSwatchImageAndColors(
      componentAttributes[parentKey]?._swatches,
    );
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        // Create a map to use option names as titles rather than "option1", "option2", etc.
        const optionNameMap: Record<string, string> = {};
        if (
          componentAttributes._options &&
          Array.isArray(componentAttributes._options)
        ) {
          componentAttributes._options.forEach((option: any) => {
            if (option.key && option.name) {
              optionNameMap[option.key] = option.name;
            }
          });
        }
        const advancedOptions: DynamicDataAttribute[] = showAdvancedAttributes
          ? [
              {
                key: "id",
                displayName: "Variant ID",
                type: "text",
              },
              {
                key: "sku",
                displayName: "Variant SKU",
                type: "text",
              },
            ]
          : [];
        const categories: DynamicDataTreeCategory[] = [
          {
            categoryName: "Variant Data",
            categoryDescription:
              "Data associated with each variant in this list.",
            parentKey,
            allowedAttributes: [
              ...advancedOptions,
              { key: "title", displayName: "Title", type: "text" },
              { key: "price", displayName: "Price", type: "price" },
              {
                key: "compareAtPrice",
                displayName: "Compare At Price",
                type: "price",
              },
              {
                key: "compareAtPriceDifference",
                displayName: "Price Difference",
                type: "price",
              },
              {
                key: "compareAtPriceDifferencePercentage",
                displayName: "Price Difference Percentage",
                type: "text",
              },
              ...Object.entries(optionNameMap).map(([key, name]) => ({
                key,
                displayName: name,
                description: getVariantOptionDescription(
                  componentAttributes,
                  key,
                ),
                type: "text" as const,
              })),
            ],
          },
        ];

        // Add variant metafields if available
        const textVariantMetafieldDefinitions =
          supportedVariantMetafields.filter((def) => {
            return textMetafieldTypes.has(def.type);
          });

        if (textVariantMetafieldDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Metafields",
            categoryDescription:
              "Metafields associated with each variant in this list.",
            parentKey,
            allowedAttributes: textVariantMetafieldDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "text",
            })),
          });
        }

        return categories;
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Variant Data",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_currentVariant",
                displayName: "Current Variant",
                description: "The variant for this item in the list",
                type: "product",
              },
            ],
          },
        ];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        if (!swatchData || swatchData.colors.length < 1) {
          return [];
        }
        const { colors } = swatchData;
        return [
          {
            categoryName: "Option Data",
            parentKey,
            allowedAttributes: colors.map((swatchName) => ({
              key: `_swatches.${swatchName}.color`,
              displayName: `${swatchName} Swatch`,
              type: "color",
            })),
          },
        ];
      },
      "text/url": (): DynamicDataTreeCategory[] => {
        return [];
      },
      list: (): DynamicDataTreeCategory[] => {
        return [];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [];
      },
      image: (): DynamicDataTreeCategory[] => {
        const swatchImages = swatchData?.images ?? [];
        const categories: DynamicDataTreeCategory[] = [
          {
            categoryName: "Variant Media",
            parentKey,
            allowedAttributes: [
              {
                key: "featuredImage",
                displayName: "Featured Image",
                description: "From the current variant",
                type: "image",
              },
              ...swatchImages.map((swatchName) => {
                return {
                  key: `_swatches.${swatchName}.image`,
                  displayName: `${swatchName} Swatch`,
                  type: "image",
                } as DynamicDataAttribute;
              }),
            ],
          },
        ];
        const imageVariantMetafieldsDefinitions =
          supportedVariantMetafields.filter((def) => {
            return imageMetafieldTypes.has(def.type);
          });

        if (imageVariantMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Metafields",
            parentKey,
            allowedAttributes: imageVariantMetafieldsDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "image",
            })),
          });
        }
        return categories;
      },
    });
  }
  // #endregion

  // #region OPTIONS LIST DYNAMIC DATA
  // If _currentOption is present, we are in a repeated option list
  if (componentAttributes._currentOption) {
    const swatchData = extractSwatchImageAndColors(
      componentAttributes._currentOption?._swatches,
    );
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Option Data",
            parentKey: "_currentOption",
            allowedAttributes: [
              {
                key: "title",
                displayName: "Title",
                description: getOptionDescription(componentAttributes, "title"),
                type: "text",
              },
              {
                key: "price",
                displayName: "Price",
                description: getOptionDescription(componentAttributes, "price"),
                type: "price",
              },
              {
                key: "compareAtPrice",
                displayName: "Compare At Price",
                description: getOptionDescription(
                  componentAttributes,
                  "compareAtPrice",
                ),
                type: "price",
              },
            ],
          },
        ];
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Product",
            parentKey: null,
            allowedAttributes: [
              { key: "_product", displayName: "Product", type: "product" },
            ],
          },
        ];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        if (!swatchData || swatchData.colors.length < 1) {
          return [];
        }
        const { colors } = swatchData;
        return [
          {
            categoryName: "Option Data",
            parentKey: "_currentOption",
            allowedAttributes: colors.map((swatchName) => ({
              key: `_swatches.${swatchName}.color`,
              displayName: `${swatchName} Swatch`,
              type: "color",
            })),
          },
        ];
      },
      "text/url": (): DynamicDataTreeCategory[] => {
        return [];
      },
      list: (): DynamicDataTreeCategory[] => {
        return [];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [];
      },
      image: (): DynamicDataTreeCategory[] => {
        if (!swatchData || swatchData.images.length < 1) {
          return [];
        }
        const { images } = swatchData;
        return [
          {
            categoryName: "Option Data",
            parentKey: "_currentOption",
            allowedAttributes: images.map((swatchName) => ({
              key: `_swatches.${swatchName}.image`,
              displayName: `${swatchName} Swatch`,
              type: "image",
            })),
          },
        ];
      },
    });
  }
  // #endregion

  // #region SELLING PLAN LIST DYNAMIC DATA
  // If _currentSellingPlan is present, we are in a repeated selling plan list
  if (parentKey && componentAttributes[parentKey]?.priceAdjustments) {
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Selling Plan Data",
            parentKey,
            allowedAttributes: [
              {
                key: "name",
                displayName: "Name",
                type: "text",
                description: "Name of the current selling plan",
              },
              {
                key: "price",
                displayName: "Price",
                type: "text",
                description: "Adjusted price of the current selling plan.",
              },
            ],
          },
        ];
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/url": (): DynamicDataTreeCategory[] => {
        return [];
      },
      list: (): DynamicDataTreeCategory[] => {
        return [];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [];
      },
      image: (): DynamicDataTreeCategory[] => {
        return [];
      },
    });
  }
  // #endregion

  // #region PRODUCT BUY BOX DYNAMIC DATA
  // If _product is present, we are certain we are inside a buy box component. Above cases should supercede
  // this where applicable (variant list, option list, selling plan list, etc.)
  if (componentAttributes._product) {
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        // Apply text-only filter for TEXT target type
        const textProductMetafieldsDefinitions =
          supportedProductMetafields.filter((def) => {
            return textMetafieldTypes.has(def.type);
          });

        const textVariantMetafieldDefinitions =
          supportedVariantMetafields.filter((def) => {
            return textMetafieldTypes.has(def.type);
          });

        const availableProductOptions =
          componentAttributes._product.options.map(
            (option: {
              key: string;
              name: string;
              values: { title: string; available: boolean }[];
            }) => ({
              key: option.key,
              name: option.name,
            }),
          );

        const advancedOptions: DynamicDataAttribute[] = showAdvancedAttributes
          ? [
              {
                key: "_product.id",
                displayName: "Product ID",
                type: "text",
              },
              {
                key: "_product.handle",
                displayName: "Product Handle",
                type: "text",
              },
              {
                key: "_variant.id",
                displayName: "Variant ID",
                type: "text",
              },
              {
                key: "_variant.sku",
                displayName: "Variant SKU",
                type: "text",
              },
            ]
          : [];
        const categories: DynamicDataTreeCategory[] = [
          {
            categoryName: "Product Data",
            categoryDescription: "Data for the current buy box product.",
            parentKey: null,
            allowedAttributes: [
              ...advancedOptions,
              {
                key: "_product.title",
                displayName: "Product Title",
                type: "text",
              },
              { key: "_variant.rawPrice", displayName: "Price", type: "price" },
              {
                key: "_variant.compareAtPrice",
                displayName: "Compare At Price",
                type: "price",
              },
              {
                key: "_variant.compareAtPriceDifference",
                displayName: "Price Difference",
                type: "price",
              },
              {
                key: "_variant.compareAtPriceDifferencePercentage",
                displayName: "Price Difference Percentage",
                type: "text",
              },
              {
                key: "_product.description",
                displayName: "Description",
                type: "text",
              },
              {
                key: "_variant.title",
                displayName: "Variant Title",
                type: "text",
              },
              {
                key: "_quantity",
                displayName: "Quantity",
                type: "text",
              },
              { key: "_product.vendor", displayName: "Vendor", type: "text" },
              { key: "_product.type", displayName: "Type", type: "text" },
              { key: "_product.handle", displayName: "Handle", type: "text" },
              ...availableProductOptions.map(
                (option: { key: string; name: string }) => ({
                  key: `_variant.${option.key}`,
                  displayName: `Selected ${option.name}`,
                  type: "text",
                }),
              ),
            ],
          },
        ];

        // Add product metafields category if there are supported product metafields
        if (textProductMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Product Metafields",
            categoryDescription:
              "Metafields associated with the buy box product.",
            parentKey: "_product",
            allowedAttributes: textProductMetafieldsDefinitions.map((def) => ({
              key: `productMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "text",
            })),
          });
        }

        // Add variant metafields category if there are supported variant metafields
        if (textVariantMetafieldDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Metafields",
            categoryDescription:
              "Metafields associated with the selected variant.",
            parentKey: "_variant",
            allowedAttributes: textVariantMetafieldDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "text",
            })),
          });
        }

        return categories;
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Product",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_product",
                displayName: "Selected Product",
                description: "From the current Buy Box",
                type: "product",
              },
            ],
          },
        ];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Product",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_variant",
                displayName: "Selected Variant",
                description: "From the current Buy Box",
                type: "product",
              },
            ],
          },
        ];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Selling Plan",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_selectedSellingPlan",
                displayName: "Selected Selling Plan",
                type: "text",
                description: "From the current Buy Box",
              },
            ],
          },
        ];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        const colorProductMetafieldsDefinitions =
          supportedProductMetafields.filter((def) => def.type === "color");
        const colorVariantMetafieldsDefinitions =
          supportedVariantMetafields.filter((def) => def.type === "color");

        const categories: DynamicDataTreeCategory[] = [];

        if (colorProductMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Product Data",
            parentKey: "_product",
            allowedAttributes: colorProductMetafieldsDefinitions.map((def) => ({
              key: `productMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "color",
            })),
          });
        }

        if (colorVariantMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Data",
            parentKey: "_variant",
            allowedAttributes: colorVariantMetafieldsDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "color",
            })),
          });
        }

        return categories;
      },

      "text/url": (): DynamicDataTreeCategory[] => {
        // Filter product and variant metafields for URL type
        const urlProductMetafieldsDefinitions =
          supportedProductMetafields.filter((def) => def.type === "url");
        const urlVariantMetafieldsDefinitions =
          supportedVariantMetafields.filter((def) => def.type === "url");

        const categories: DynamicDataTreeCategory[] = [];

        // Add product URL metafields if available
        if (urlProductMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Product Metafields",
            categoryDescription: "URL metafields for the current product",
            parentKey: "_product",
            allowedAttributes: urlProductMetafieldsDefinitions.map((def) => ({
              key: `productMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "url",
            })),
          });
        }

        // Add variant URL metafields if available
        if (urlVariantMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Metafields",
            categoryDescription: "URL metafields for the selected variant",
            parentKey: "_variant",
            allowedAttributes: urlVariantMetafieldsDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "url",
            })),
          });
        }

        return categories;
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Product Data",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_quantity",
                displayName: "Selected Quantity",
                type: "number",
                description: "From the current Buy Box",
              },
            ],
          },
        ];
      },
      list: (): DynamicDataTreeCategory[] => {
        // NOTE (Matt 2025-03-25): We don't support Product.Options inside of iterable components
        // like the SelectionList because some of the formatting that is useful to the Options
        // data is done inside of OptionSelect. For now we'll continue this, unless users seem
        // to be needing to use SelectionList or Tabs instead of OptionSelect.
        return [
          {
            categoryName: "Product Data",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_product.variants",
                displayName: "Variants",
                type: "list",
                description: "From the current Buy Box",
              },
              {
                key: "_sellingPlans",
                displayName: "Selling Plans",
                type: "list",
                description: "From the current Buy Box",
              },
            ],
          },
        ];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        const selectedOptionValues: Record<
          string,
          { _swatches?: Record<string, SwatchValue>; [key: string]: any }
        > = componentAttributes._selectedOptionValues ?? {};
        const swatchOptionImageLists = Object.entries(
          selectedOptionValues,
        ).reduce((lists: DynamicDataAttribute[], [optionName, optionValue]) => {
          if (optionValue?._swatches) {
            const imageLists = extractSwatchImageLists(
              optionValue._swatches,
              "_selectedOptionValues",
              optionName,
            );
            lists.push(...imageLists);
          }
          return lists;
        }, []);

        const swatchVariantImageLists = componentAttributes._variant
          ? extractSwatchImageLists(
              componentAttributes._variant._swatches,
              "_variant",
            )
          : [];
        return [
          {
            categoryName: "Image Lists",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_product.images",
                displayName: "Product Images",
                description: "Images for the current product",
                type: "image",
              },
              ...swatchOptionImageLists,
              ...swatchVariantImageLists,
            ],
          },
        ];
      },
      image: (): DynamicDataTreeCategory[] => {
        const imageProductMetafieldsDefinitions =
          supportedProductMetafields.filter((def) => {
            return imageMetafieldTypes.has(def.type);
          });
        const imageVariantMetafieldsDefinitions =
          supportedVariantMetafields.filter((def) => {
            return imageMetafieldTypes.has(def.type);
          });

        const categories: DynamicDataTreeCategory[] = [
          {
            categoryName: "Product Media",
            categoryDescription: "Media for the current buy box product.",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_product.featured_image",
                displayName: "Product Featured Image",
                description: "From the buy box product",
                type: "image",
              },
              {
                key: "_variant.featuredImage",
                displayName: "Variant Featured Image",
                description: "From the selected variant",
                type: "image",
              },
            ],
          },
        ];

        if (imageProductMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Product Metafields",
            categoryDescription:
              "Image metafields for the current buy box product",
            parentKey: "_product",
            allowedAttributes: imageProductMetafieldsDefinitions.map((def) => ({
              key: `productMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "image",
            })),
          });
        }

        if (imageVariantMetafieldsDefinitions.length > 0) {
          categories.push({
            categoryName: "Variant Metafields",
            categoryDescription:
              "Image metafields for the selected variant in the current buy box",
            parentKey: "_variant",
            allowedAttributes: imageVariantMetafieldsDefinitions.map((def) => ({
              key: `variantMetafields.${def.namespace}.${def.key}`,
              displayName: def.name,
              description: `${def.namespace}`,
              type: "image",
            })),
          });
        }

        return categories;
      },
    });
  }
  // #endregion

  // #region TEMPLATE PRODUCT DYNAMIC DATA
  // If _templateProduct is present, we're on a PDP. Currently only supports selecting
  // 'template product' for product/integration components
  if (componentAttributes._templateProduct) {
    return exhaustiveSwitch({ type: targetDataType })({
      text: (): DynamicDataTreeCategory[] => {
        return [];
      },
      productOnly: (): DynamicDataTreeCategory[] => {
        return [
          {
            categoryName: "Product",
            parentKey: null,
            allowedAttributes: [
              {
                key: "_templateProduct",
                displayName: "Template Product",
                type: "product",
                description: "From current page",
              },
            ],
          },
        ];
      },
      productVariant: (): DynamicDataTreeCategory[] => {
        return [];
      },
      sellingPlan: (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/color": (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/url": (): DynamicDataTreeCategory[] => {
        return [];
      },
      "text/integer": (): DynamicDataTreeCategory[] => {
        return [];
      },
      quantity: (): DynamicDataTreeCategory[] => {
        return [];
      },
      list: (): DynamicDataTreeCategory[] => {
        return [];
      },
      imageList: (): DynamicDataTreeCategory[] => {
        return [];
      },
      image: (): DynamicDataTreeCategory[] => {
        return [];
      },
    });
  }
  // #endregion

  return [];
};

export function getDynamicDataTypeIcon(type: DynamicDataType): React.ReactNode {
  return exhaustiveSwitch({ type })({
    text: <TextIcon />,
    price: <CurrencyIcon />,
    image: <ImageIcon />,
    number: <HashIcon />,
    product: <ProductIcon />,
    list: <ListIcon />,
    color: <ColorIcon />,
    url: <LinkIcon />,
  });
}

/**
 * Creates a description string for an option by joining its values with forward slashes
 * @param componentAttributes - Component attributes containing options data
 * @returns A string of option values separated by " / " or empty string if not found
 */
const getOptionDescription = (
  componentAttributes: Context["attributes"],
  targetValueKey: string,
): string | undefined => {
  if (
    !componentAttributes?._currentOption?.title ||
    !componentAttributes?._options ||
    !Array.isArray(componentAttributes._options)
  ) {
    return undefined;
  }

  // Get the current option title (value title)
  const currentOptionValueTitle = componentAttributes._currentOption.title;

  // Find which option this value belongs to by checking each option's values array
  for (const option of componentAttributes._options) {
    const valueExists = option.values.some(
      (value: { title: string }) => value.title === currentOptionValueTitle,
    );

    if (valueExists) {
      return option.values
        .map((value: any) => value[targetValueKey])
        .join(" / ");
    }
  }

  return undefined;
};

/**
 * Creates a description string for an option in a variant list by joining all possible values
 * @param componentAttributes - Component attributes containing options data
 * @param optionKey - The option key (e.g., "option1", "option2")
 * @returns A string of option values separated by " / " or empty string if not found
 */
const getVariantOptionDescription = (
  componentAttributes: Context["attributes"],
  optionKey: string,
): string | undefined => {
  if (
    !componentAttributes?._options ||
    !Array.isArray(componentAttributes._options)
  ) {
    return undefined;
  }

  // Find the option object that matches the given key
  const option = componentAttributes._options.find(
    (option) => option.key === optionKey,
  );

  if (!option || !option.values || !Array.isArray(option.values)) {
    return undefined;
  }

  return option.values
    .map((value: { title: string }) => value.title)
    .join(" / ");
};
